import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, BtnSecondary, formatDate, formatDateLong, formatCurrency } from '../../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const card = {
  background: '#fff',
  border: `1px solid ${S.borderLight}`,
  borderRadius: 12,
  padding: 18,
  marginBottom: 14,
};

const sectionTitle = {
  fontSize: 11,
  fontWeight: 700,
  color: S.textMuted,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  marginBottom: 10,
};

function ChangeArrow({ type }) {
  if (type === 'increased' || type === 'added') {
    return <span style={{ color: '#059669', fontWeight: 700 }}>↑</span>;
  }
  if (type === 'decreased' || type === 'removed') {
    return <span style={{ color: '#dc2626', fontWeight: 700 }}>↓</span>;
  }
  return <span style={{ color: S.textMuted }}>—</span>;
}

function fmtMoney(v) {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(String(v).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(n)) return String(v);
  // formatCurrency respects tenant currency + active i18n language for
  // grouping/decimal marks. Previously used `.toLocaleString(undefined,…)`
  // which silently picked the browser default locale (Test Run 7-22:
  // French UI showed comma-separated thousands via en-US).
  return formatCurrency(n);
}

function fmtPct(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

const PolicyComparisonPanel = ({ clientId, comparisonId, onClose }) => {
  const { t, i18n } = useTranslation();
  const [row, setRow] = useState(null);
  const [error, setError] = useState('');
  const [rerunning, setRerunning] = useState(false);

  const fetchRow = useCallback(async () => {
    try {
      const r = await axios.get(`${BASE_URL}/clients/${clientId}/comparisons/${comparisonId}`, { withCredentials: true });
      setRow(r.data?.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || t('clientsModule.comparison.loadFailed'));
    }
  }, [clientId, comparisonId]);

  const rerun = useCallback(async () => {
    if (rerunning) return;
    setRerunning(true);
    try {
      const r = await axios.post(
        `${BASE_URL}/clients/${clientId}/comparisons/${comparisonId}/rerun`,
        { locale: (i18n?.language || 'en').split('-')[0] },
        { withCredentials: true },
      );
      setRow(r.data?.data || null);
    } catch (e) {
      alert(e?.response?.data?.message || t('clientsModule.comparison.rerunFailed'));
    } finally {
      setRerunning(false);
    }
  }, [clientId, comparisonId, rerunning]);

  useEffect(() => { fetchRow(); }, [fetchRow]);

  // Poll while pending/processing — comparison runs in the background.
  useEffect(() => {
    if (!row || (row.status !== 'pending' && row.status !== 'processing')) return;
    const t = setTimeout(fetchRow, 2000);
    return () => clearTimeout(t);
  }, [row, fetchRow]);

  if (error) {
    return (
      <div style={card}>
        <div style={{ fontSize: 13, color: '#b91c1c' }}>{error}</div>
        <div style={{ marginTop: 12 }}><BtnSecondary onClick={onClose}>{t('clientsModule.comparison.close')}</BtnSecondary></div>
      </div>
    );
  }
  if (!row) {
    return (
      <div style={card}>
        <div style={{ fontSize: 13, color: S.textMuted }}>{t('clientsModule.comparison.loading')}</div>
      </div>
    );
  }

  const status = row.status;
  const expDoc = row.expiringDocumentId;
  const renDoc = row.renewalDocumentId;
  const expName = expDoc?.originalName || expDoc?.name || t('clientsModule.comparison.expiringFallback');
  const renName = renDoc?.originalName || renDoc?.name || t('clientsModule.comparison.renewalFallback');

  if (status === 'pending' || status === 'processing') {
    return (
      <div style={card}>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 18, height: 18, border: '2px solid #e2e8f0', borderTopColor: S.burgundy, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          <div style={{ fontSize: 14, color: S.textPrimary }}>{t('clientsModule.comparison.analyzing')}</div>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: S.textMuted }}>
          {t('clientsModule.comparison.comparingHint', { expiring: expName, renewal: renName })}
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div style={{ ...card, borderColor: '#fecaca', background: '#fef2f2' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#b91c1c', marginBottom: 6 }}>{t('clientsModule.comparison.failedTitle')}</div>
        <div style={{ fontSize: 13, color: '#7f1d1d' }}>{row.errorMessage || t('clientsModule.comparison.unknownError')}</div>
        <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
          <BtnSecondary onClick={rerun} disabled={rerunning}>{rerunning ? t('clientsModule.comparison.rerunning') : t('clientsModule.comparison.rerun')}</BtnSecondary>
          <BtnSecondary onClick={onClose}>{t('clientsModule.comparison.close')}</BtnSecondary>
        </div>
      </div>
    );
  }

  const c = row.comparison || {};
  const pc = c.premiumChange || {};
  const hasChanges = (c.coverageChanges?.length || 0) > 0
    || (c.formsAdded?.length || 0) > 0
    || (c.formsRemoved?.length || 0) > 0
    || (c.exclusions?.length || 0) > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ ...card, paddingBottom: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{t('clientsModule.comparison.comparisonTitle')}</div>
            <div style={{ fontSize: 12, color: S.textMuted }}>
              <strong>{expName}</strong>
              {expDoc?.createdAt ? ` (${formatDate(typeof expDoc.createdAt === 'string' ? expDoc.createdAt.slice(0, 10) : expDoc.createdAt)})` : ''}
              {' '}{t('clientsModule.detail.vs')}{' '}
              <strong>{renName}</strong>
              {renDoc?.createdAt ? ` (${formatDate(typeof renDoc.createdAt === 'string' ? renDoc.createdAt.slice(0, 10) : renDoc.createdAt)})` : ''}
            </div>
            {row.comparedAt && (
              <div style={{ fontSize: 11, color: S.textMuted, marginTop: 2 }}>
                {t('clientsModule.comparison.analyzed', { when: formatDateLong(row.comparedAt) })}
              </div>
            )}
          </div>
          <BtnSecondary onClick={onClose}>{t('clientsModule.comparison.close')}</BtnSecondary>
        </div>
      </div>

      {/* Summary card */}
      {c.summary && (
        <div style={{ ...card, background: hasChanges ? '#fffbeb' : '#f0fdf4', borderColor: hasChanges ? '#fde68a' : '#bbf7d0' }}>
          <div style={sectionTitle}>{t('clientsModule.comparison.summary')}</div>
          <div style={{ fontSize: 14, color: S.textPrimary, lineHeight: 1.5 }}>{c.summary}</div>
        </div>
      )}

      {/* Premium card */}
      <div style={card}>
        <div style={sectionTitle}>{t('clientsModule.comparison.totalPremium')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, fontSize: 14 }}>
          <div>
            <div style={{ fontSize: 11, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('clientsModule.comparison.expiring')}</div>
            <div style={{ fontWeight: 700, color: S.textPrimary }}>{fmtMoney(pc.expiring)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('clientsModule.comparison.renewal')}</div>
            <div style={{ fontWeight: 700, color: S.textPrimary }}>{fmtMoney(pc.renewal)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{t('clientsModule.comparison.change')}</div>
            <div style={{ fontWeight: 700, color: pc.difference > 0 ? '#dc2626' : pc.difference < 0 ? '#059669' : S.textMuted }}>
              {pc.difference != null ? `${pc.difference > 0 ? '+' : ''}${fmtMoney(pc.difference)} (${fmtPct(pc.percentChange)})` : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* Coverage changes */}
      {(c.coverageChanges?.length || 0) > 0 && (
        <div style={card}>
          <div style={sectionTitle}>{t('clientsModule.comparison.coverageChanges')}</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: `1px solid ${S.borderLight}` }}>
                <th style={{ textAlign: 'left', padding: '8px 0', color: S.textMuted, fontWeight: 600 }}>{t('clientsModule.comparison.colCoverage')}</th>
                <th style={{ textAlign: 'left', padding: '8px 0', color: S.textMuted, fontWeight: 600 }}>{t('clientsModule.comparison.colExpiring')}</th>
                <th style={{ textAlign: 'left', padding: '8px 0', color: S.textMuted, fontWeight: 600 }}>{t('clientsModule.comparison.colRenewal')}</th>
                <th style={{ textAlign: 'left', padding: '8px 0', color: S.textMuted, fontWeight: 600 }}>{t('clientsModule.comparison.colChange')}</th>
              </tr>
            </thead>
            <tbody>
              {c.coverageChanges.map((cc, i) => {
                const unchanged = cc.changeType === 'unchanged';
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${S.borderLight}`, background: unchanged ? 'transparent' : '#fff7ed' }}>
                    <td style={{ padding: '10px 0', color: unchanged ? S.textMuted : S.textPrimary, fontWeight: unchanged ? 400 : 600 }}>{cc.field}</td>
                    <td style={{ padding: '10px 0', color: unchanged ? S.textMuted : S.textPrimary }}>{cc.expiring}</td>
                    <td style={{ padding: '10px 0', color: unchanged ? S.textMuted : S.textPrimary }}>{cc.renewal}</td>
                    <td style={{ padding: '10px 0' }}>
                      <ChangeArrow type={cc.changeType} /> <span style={{ color: S.textMuted, marginLeft: 4, textTransform: 'capitalize' }}>{cc.changeType}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Forms side-by-side */}
      {((c.formsAdded?.length || 0) > 0 || (c.formsRemoved?.length || 0) > 0) && (
        <div style={card}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
            <div>
              <div style={sectionTitle}>{t('clientsModule.comparison.formsRemoved', { count: c.formsRemoved?.length || 0 })}</div>
              {(c.formsRemoved || []).map((f, i) => (
                <div key={i} style={{ padding: '8px 0', fontSize: 13, color: S.textPrimary, borderBottom: `1px solid ${S.borderLight}` }}>
                  <span style={{ color: '#dc2626', fontWeight: 700, marginRight: 8 }}>✕</span>
                  <strong>{f.formNumber}</strong> {f.formTitle && `— ${f.formTitle}`}
                </div>
              ))}
              {(c.formsRemoved || []).length === 0 && <div style={{ fontSize: 13, color: S.textMuted }}>{t('clientsModule.comparison.none')}</div>}
            </div>
            <div>
              <div style={sectionTitle}>{t('clientsModule.comparison.formsAdded', { count: c.formsAdded?.length || 0 })}</div>
              {(c.formsAdded || []).map((f, i) => (
                <div key={i} style={{ padding: '8px 0', fontSize: 13, color: S.textPrimary, borderBottom: `1px solid ${S.borderLight}` }}>
                  <span style={{ color: '#059669', fontWeight: 700, marginRight: 8 }}>✓</span>
                  <strong>{f.formNumber}</strong> {f.formTitle && `— ${f.formTitle}`}
                </div>
              ))}
              {(c.formsAdded || []).length === 0 && <div style={{ fontSize: 13, color: S.textMuted }}>{t('clientsModule.comparison.none')}</div>}
            </div>
          </div>
        </div>
      )}

      {/* Endorsements */}
      {(c.endorsementsChanged?.length || 0) > 0 && (
        <div style={card}>
          <div style={sectionTitle}>{t('clientsModule.comparison.endorsementsChanged')}</div>
          {c.endorsementsChanged.map((e, i) => (
            <div key={i} style={{ padding: '10px 0', borderBottom: i < c.endorsementsChanged.length - 1 ? `1px solid ${S.borderLight}` : 'none', fontSize: 13 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: e.changeType === 'added' ? '#dcfce7' : e.changeType === 'removed' ? '#fee2e2' : '#fef3c7', color: e.changeType === 'added' ? '#166534' : e.changeType === 'removed' ? '#991b1b' : '#92400e', textTransform: 'uppercase' }}>{e.changeType}</span>
                <strong style={{ color: S.textPrimary }}>{e.name}</strong>
              </div>
              {e.details && <div style={{ color: S.textSecondary, paddingLeft: 4 }}>{e.details}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Exclusions */}
      {(c.exclusions?.length || 0) > 0 && (
        <div style={card}>
          <div style={sectionTitle}>{t('clientsModule.comparison.exclusions')}</div>
          {c.exclusions.map((x, i) => (
            <div key={i} style={{ padding: '8px 0', fontSize: 13, color: S.textPrimary, display: 'flex', alignItems: 'flex-start', gap: 10, borderBottom: i < c.exclusions.length - 1 ? `1px solid ${S.borderLight}` : 'none' }}>
              <span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700, background: x.changeType === 'added' ? '#fee2e2' : '#dcfce7', color: x.changeType === 'added' ? '#991b1b' : '#166534', whiteSpace: 'nowrap' }}>{x.changeType === 'added' ? t('clientsModule.comparison.badgeNew') : t('clientsModule.comparison.badgeRemoved')}</span>
              <span>{x.description}</span>
            </div>
          ))}
        </div>
      )}

      {/* Warnings */}
      {(c.warnings?.length || 0) > 0 && (
        <div style={{ ...card, background: '#fffbeb', borderColor: '#fde68a' }}>
          <div style={{ ...sectionTitle, color: '#92400e' }}>{t('clientsModule.comparison.warnings')}</div>
          {c.warnings.map((w, i) => (
            <div key={i} style={{ padding: '6px 0', fontSize: 13, color: '#78350f' }}>• {w}</div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <BtnSecondary disabled title={t('clientsModule.comparison.downloadComingSoon')}>{t('clientsModule.comparison.downloadReport')}</BtnSecondary>
        <BtnSecondary onClick={rerun} disabled={rerunning} title={t('clientsModule.comparison.rerunTitle')}>
          {rerunning ? t('clientsModule.comparison.rerunning') : t('clientsModule.comparison.rerun')}
        </BtnSecondary>
        <BtnSecondary onClick={onClose}>{t('clientsModule.comparison.newComparison')}</BtnSecondary>
      </div>
    </div>
  );
};

export default PolicyComparisonPanel;
